import axios from '@axios'
import Vue from 'vue'
import { saveAs } from 'file-saver'

const state = () => ({
  quotes: {
    meta: {
      pagination: {
        page: 1,
        per_page: 10,
        total_objects: 0,
      },
    },
    data: [],
  },
  biddingQuotes: {
    meta: {
      pagination: {
        page: 1,
        per_page: 10,
        total_objects: 0,
      },
    },
    data: [],
  },
  quote: {
    address: {},
    quote_items_attributes: [],
  },
  supplierQuoteItems: {
    meta: {
      pagination: {
        page: 1,
        per_page: 10,
        total_objects: 0,
      },
    },
    data: [],
  },
  quotePayments: {
    meta: {
      pagination: {
        page: 1,
        per_page: 10,
        total_objects: 0,
      },
    },
    data: [],
  },
  itemDeliveries: {
    quoteId: null,
    delivery_status: null,
    items: [],
  },
})

const getters = {
  quotes(state) {
    return state.quotes.data
  },
  quotesPagination(state) {
    return state.quotes.meta.pagination
  },
  itemDeliveriesByQuoteItem(state) {
    return state.itemDeliveries.items.reduce((map, row) => ({ ...map, [row.quote_item_id]: row }), {})
  },
  itemDeliveriesQuoteId(state) {
    return state.itemDeliveries.quoteId
  },
  quote(state) {
    return state.quote
  },
  quoteIncome(state) {
    return state.quote.income
  },
  biddingQuotes(state) {
    return state.biddingQuotes.data
  },
  supplierQuoteItems(state) {
    return state.supplierQuoteItems.data
  },
  quotePayments(state) {
    return state.quotePayments.data
  },
}

const mutations = {
  ADD_QUOTE(state, quote) {
    state.quotes.push(quote)
  },
  SET_ITEM_DELIVERIES(state, { quoteId, payload }) {
    state.itemDeliveries = { quoteId, delivery_status: payload.delivery_status, items: payload.items || [] }
  },
  SET_QUOTES(state, payload) {
    state.quotes = payload
  },
  CHANGE_PAGINATION(state, { field, value }) {
    state.quotes.meta.pagination[field] = value
  },
  CLEAR_QUOTES(state) {
    state.quotes = {
      meta: {
        pagination: {
          page: 1,
          per_page: 10,
          total_objects: 0,
        },
      },
      data: [],
    }
  },
  SET_QUOTE(state, payload) {
    state.quote = payload
  },
  SET_QUOTE_FIELD(state, { field, value }) {
    Vue.set(state.quote, field, value)
  },
  SET_BIDDING_QUOTES(state, payload) {
    state.biddingQuotes = payload
  },
  SET_SUPPLIER_QUOTE_ITEMS(state, payload) {
    state.supplierQuoteItems = payload
  },
  SET_QUOTE_ITEMS(state, items) {
    state.quote.quote_items_attributes = items
  },
  SET_QUOTE_PAYMENTS(state, items) {
    state.quotePayments = items
  },
}

// Una cotizacion pagada, aprobada a credito o esperando validacion vive en Mis compras; el
// resto en Cotizaciones. La ruta no puede declararlo porque depende del estado de pago, asi
// que se publica en cuanto se conoce y el menu lateral marca la seccion correcta.
const PURCHASED_PAYMENT_STATUSES = ['awaiting_payment_approval', 'payment_approved', 'credit_approved']

const markQuoteSection = (context, quote) => {
  if (!quote?.id) return

  const section = PURCHASED_PAYMENT_STATUSES.includes(quote.payment_status) ? 'approvedQuotes' : 'quotes'
  context.commit('app/SET_ACTIVE_NAV_LINK', section, { root: true })
}

const actions = {
  // Hay pantallas de una cotizacion que no la piden (las remisiones, por ejemplo) porque
  // les basta con su id. Si la cotizacion ya esta en el store, la seccion del menu se sabe
  // sin pedir nada.
  markSectionForRoute(context, quoteId) {
    const { quote } = context.state
    if (!quoteId || quote?.id !== quoteId) return

    markQuoteSection(context, quote)
  },

  // Para las pantallas que solo usan el id: sin la cotizacion en el store, el encabezado se
  // queda sin obra y sin folio al entrar por liga directa o al recargar.
  ensureQuote(context, quoteId) {
    if (!quoteId) return Promise.resolve(null)

    const { quote } = context.state
    if (quote?.id === quoteId) {
      markQuoteSection(context, quote)
      return Promise.resolve(quote)
    }

    return context.dispatch('fetchQuote', quoteId).catch(() => null)
  },

  fetchQuotes(context, params) {
    return new Promise((resolve, reject) => {
      axios
        .get('/v1/quotes', {
          params,
        })
        .then(response => {
          context.commit('SET_QUOTES', response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  addQuote(context, quote) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/quotes', {
          quote,
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  fetchQuote(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}`, {
          skipErrorToast: true,
        })
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          markQuoteSection(context, response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  fetchQuoteMetadata(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/metadata`)
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          markQuoteSection(context, response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  async updateQuoteOpenQuotation(context, { quoteId, openQuotation }) {
    const response = await axios.patch(`/v1/quotes/${quoteId}/update_open_quotation`, {
      open_quotation: openQuotation,
    })

    // El listado tambien dispara esta accion, asi que el store solo se toca cuando la
    // cotizacion cargada es justo esta; si no, se estaria pisando otra.
    if (String(context.state.quote?.id) === String(quoteId)) {
      context.commit('SET_QUOTE_FIELD', { field: 'open_quotation', value: response.data.open_quotation })
      context.commit('SET_QUOTE_FIELD', { field: 'open_quotation_enabled', value: response.data.open_quotation_enabled })
      context.commit('SET_QUOTE_FIELD', { field: 'open_quotation_overridden', value: response.data.open_quotation_overridden })
    }

    return response.data
  },
  async updateQuoteComparativeLayout(context, { quoteId, comparativeLayout }) {
    const response = await axios.patch(`/v1/quotes/${quoteId}/update_comparative_layout`, {
      comparative_layout: comparativeLayout,
    })

    // Igual que la apertura: el listado tambien puede disparar esto, asi que el store solo
    // se toca cuando la cotizacion cargada es justo esta.
    if (String(context.state.quote?.id) === String(quoteId)) {
      context.commit('SET_QUOTE_FIELD', { field: 'comparative_layout', value: response.data.comparative_layout })
    }

    return response.data
  },
  fetchAuctionScore(_, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/auction_score`)
        .then(response => resolve(response.data.data))
        .catch(error => reject(error.response))
    })
  },
  fetchCommissionSuggestion(_, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/commission_suggestion`)
        .then(response => resolve(response.data.data))
        .catch(error => reject(error.response))
    })
  },
  applyQuoteAdjustments(_, { quoteId, items }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/apply_adjustments`, { items })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },
  editQuote(context, quote) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quote.id}`, {
          quote,
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  publishQuote(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/publish`, {
          is_published: true,
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  buyoutQuote(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/buyout`, {
          quote_status: 'buyout',
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  closeQuoteBidding(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/close_quote_bidding`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  deleteQuote(context, payload) {
    const quoteId = typeof payload === 'object' ? payload.quoteId : payload
    const deleteReason = typeof payload === 'object' ? payload.deleteReason : null

    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}`, {
          quote: {
            quote_status: 'deleted',
            delete_reason: deleteReason,
          },
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  restoreQuote(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/v1/quotes/${quoteId}/restore`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  requoteQuote(_, payload) {
    const quoteId = payload && typeof payload === 'object' ? payload.quoteId : payload
    const mode = payload && typeof payload === 'object' ? payload.mode : undefined
    return new Promise((resolve, reject) => {
      axios
        .post(`/v1/quotes/${quoteId}/requote`, mode ? { mode } : {})
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  setQuoteBidding(_, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/bidding`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  getBiddingQuotes(context, params) {
    return new Promise((resolve, reject) => {
      axios
        .get('/v1/quotes/quotes_bidding', {
          params,
        })
        .then(response => {
          context.commit('SET_QUOTES', response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  getQuotesWon(context, { winnerBranch }) {
    return new Promise((resolve, reject) => {
      axios
        .get('/v1/quotes/quotes_won', {
          params: {
            by_winner_branch: winnerBranch,
          },
        })
        .then(response => {
          context.commit('SET_QUOTES', response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  fetchQuoteBidding(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/quote_bidding`)
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  fetchSupplierBranchQuoteItems(context, { quoteId = null, branchId = null }) {
    return new Promise((resolve, reject) => {
      axios
        .get('/v1/supplier_branch_quote_items', {
          params: {
            by_quote: quoteId,
            by_supplier_branch: branchId,
          },
        })
        .then(response => {
          context.commit('SET_SUPPLIER_QUOTE_ITEMS', response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  bidInQuote(_, { quoteId, supplier_branch_quote_items_records }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/bid_in_quote`, {
          quote: { supplier_branch_quote_items_records },
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  acceptQuote(_, { quoteId }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/accept_bid`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  rejectQuote(_, { quoteId }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/reject_bid`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  payQuote(_, { quoteId, attachment }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/pay_quote`, {
          quote: {
            payment_attachment: attachment,
          },
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  fetchQuoteTaxInformationOptions(_, { quoteId }) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/tax_information_options`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response || error)
        })
    })
  },
  setQuoteTaxInformation(context, { quoteId, taxInformationId }) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/v1/quotes/${quoteId}/set_tax_information`, {
          tax_information_id: taxInformationId,
        })
        .then(response => {
          if (context.state.quote?.id === quoteId) {
            context.commit('SET_QUOTE', response.data)
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response || error)
        })
    })
  },
  approvePayment(context, { quoteId, selectedPaymentMode = null, applyCreditSurcharge = null }) {
    return new Promise((resolve, reject) => {
      const payload = {}

      if (selectedPaymentMode) {
        payload.selected_payment_mode = selectedPaymentMode
      }

      if (applyCreditSurcharge !== null && applyCreditSurcharge !== undefined) {
        payload.apply_credit_surcharge = applyCreditSurcharge
      }

      axios
        .put(`/v1/quotes/${quoteId}/approve_payment`, payload)
        .then(response => {
          resolve(response.data)
          context.commit('SET_QUOTE', response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  rejectPayment(context, { quoteId }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/reject_payment`)
        .then(response => {
          resolve(response.data)
          context.commit('SET_QUOTE', response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  creditPayment(context, { quoteId, applyCreditSurcharge = true }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/credit_payment`, {
          apply_credit_surcharge: applyCreditSurcharge,
        })
        .then(response => {
          resolve(response.data)
          context.commit('SET_QUOTE', response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  getQuoteInovices(context, { quoteId }) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/invoices.json`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  downloadQuoteInovices(_, quote) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quote.id}/invoices.zip`, {
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/octet-stream',
          })
          const filename = `facturas-cotizacion-folio-${quote.folio}.zip`
          saveAs(blob, filename)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  createQuoteInovice(context, { quoteId, attachments }) {
    return new Promise((resolve, reject) => {
      axios
        .post(`/v1/quotes/${quoteId}/invoices`, attachments, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  deleteQuoteInovice(context, { quoteId, invoiceId }) {
    return new Promise((resolve, reject) => {
      axios
        .delete(`/v1/quotes/${quoteId}/invoices/${invoiceId}`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  downloadQuotes(_, params) {
    return new Promise((resolve, reject) => {
      axios
        .get('/v1/quotes.zip', {
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'application/json',
          },
          params,
        })
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/octet-stream',
          })
          const filename = 'facturas-cotizacions.zip'
          saveAs(blob, filename)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },

  deleteQuoteItems(context, { quoteId, items }) {
    return new Promise((resolve, reject) => {
      axios
        .delete(`/v1/quotes/${quoteId}/delete_quote_items`, {
          data: {
            quote: {
              quote_items_attributes: items,
            },
          },
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  getQuotePayments(context, { quoteId }) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/v1/quotes/${quoteId}/payments`)
        .then(response => {
          context.commit('SET_QUOTE_PAYMENTS', response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  uploadQuotePayment(context, { quoteId, payment }) {
    return new Promise((resolve, reject) => {
      axios
        .post(`/v1/quotes/${quoteId}/payments`, { payment })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  deleteQuotePayment(context, { quoteId, paymentId }) {
    return new Promise((resolve, reject) => {
      axios
        .delete(`/v1/quotes/${quoteId}/payments/${paymentId}`)
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  updateOrderNumber(context, { quoteId, quoteItemId, orderNumber }) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/v1/quotes/${quoteId}/update_quote_item_order_number`, {
          quote_item_id: quoteItemId,
          order_number: orderNumber,
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  updateDeliveryStatus(context, { quoteId, delivery_status }) {
    return new Promise((resolve, reject) => {
      axios
        .put(`/v1/quotes/${quoteId}/update_delivery_status`, {
          delivery_status,
        })
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  removeFreight(context, quoteId) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/v1/quotes/${quoteId}/remove_freight`)
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  updateFreight(context, { quoteId, freightOverrides }) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/v1/quotes/${quoteId}/update_freight`, { freight_overrides: freightOverrides })
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  clearFreightOverride(context, { quoteId, supplierBranchId }) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/v1/quotes/${quoteId}/clear_freight_override`, { supplier_branch_id: supplierBranchId })
        .then(response => {
          context.commit('SET_QUOTE', response.data)
          // La cotizacion trae su obra. Se guarda aqui para que las pantallas que solo
          // llevan el id de la cotizacion en la url tengan el menu y el encabezado
          // completos aunque se entre por liga directa.
          if (response.data?.project_attributes?.id) {
            context.commit('projects/SET_PROJECT', response.data.project_attributes, { root: true })
          }
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response)
        })
    })
  },
  payWithWallet(_, {
    quoteId,
    amount,
    idempotencyKey = null,
    idempotency_key: legacyIdempotencyKey = null,
    note = null,
  }) {
    return axios.post(`/v1/quotes/${quoteId}/pay_with_wallet`, {
      amount,
      idempotency_key: idempotencyKey || legacyIdempotencyKey,
      note,
    })
  },

  fetchQuoteItemDeliveries(context, quoteId) {
    return axios.get(`/v1/quotes/${quoteId}/item_deliveries`).then(response => {
      context.commit('SET_ITEM_DELIVERIES', { quoteId, payload: response.data })
      return response.data
    })
  },

  registerQuoteItemDelivery(context, {
    quoteId, quoteItemId, quantity, deliveredAt, note,
  }) {
    return axios
      .post(`/v1/quotes/${quoteId}/quote_items/${quoteItemId}/deliveries`, {
        quantity,
        delivered_at: deliveredAt,
        note,
      }, { skipErrorToast: true })
      .then(response => {
        context.commit('SET_ITEM_DELIVERIES', { quoteId, payload: response.data })
        return response.data
      })
  },

  deleteQuoteItemDelivery(context, { quoteId, quoteItemId, deliveryId }) {
    return axios
      .delete(`/v1/quotes/${quoteId}/quote_items/${quoteItemId}/deliveries/${deliveryId}`, { skipErrorToast: true })
      .then(response => {
        context.commit('SET_ITEM_DELIVERIES', { quoteId, payload: response.data })
        return response.data
      })
  },

  updateQuoteItemPrices(_, {
    quoteId,
    quoteItemId,
    supplierBranchId,
    unitPrice,
    unitCommissionPrice,
    customerUnitPrice,
    quantity = null,
    productCatalogId,
  }) {
    return axios.patch(`/v1/quotes/${quoteId}/update_quote_item_prices`, {
      quote_item_id: quoteItemId,
      ...(productCatalogId ? { product_catalog_id: productCatalogId } : {}),
      ...(supplierBranchId !== undefined ? { supplier_branch_id: supplierBranchId } : {}),
      unit_price: unitPrice,
      ...(unitCommissionPrice !== undefined ? { unit_commission_price: unitCommissionPrice } : {}),
      ...(customerUnitPrice !== undefined ? { customer_unit_price: customerUnitPrice } : {}),
      ...(quantity !== null && quantity !== undefined ? { quantity } : {}),
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
